import { ChevronRight, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import type { ShellNavigationItem } from "../types/shell.js";

export function MobileNavigationMenu({
  items,
  onClose,
}: {
  items: readonly ShellNavigationItem[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = panel.current?.querySelectorAll<HTMLElement>("a[href], button");
      if (!controls?.length) return;
      const first = controls[0]!;
      const last = controls[controls.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const desktop = window.matchMedia?.("(min-width: 768px)");
    const onResize = () => {
      if (desktop?.matches) onClose();
    };
    desktop?.addEventListener("change", onResize);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKeyDown);
      desktop?.removeEventListener("change", onResize);
    };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-50 md:hidden">
      <button
        type="button"
        aria-label={t("nav.closeMenu")}
        tabIndex={-1}
        className="absolute inset-0 bg-foreground/45 backdrop-blur-sm"
        data-control-id="nav.mobile.menu.backdrop"
        onClick={onClose}
      />
      <section
        aria-modal="true"
        aria-labelledby="mobile-menu-title"
        role="dialog"
        id="mobile-navigation-menu"
        ref={panel}
        data-control-id="nav.mobile.menu"
        className="absolute inset-x-0 bottom-0 flex max-h-[85dvh] min-h-[45dvh] flex-col rounded-t-3xl border-t border-border bg-popover text-popover-foreground shadow-2xl"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-border px-6 py-5">
          <h2 id="mobile-menu-title" className="text-xl font-semibold">
            {t("nav.menu")}
          </h2>
          <button
            type="button"
            aria-label={t("common.close")}
            onClick={onClose}
            data-control-id="nav.mobile.menu.close"
            className="grid size-11 place-items-center rounded-full bg-muted text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </header>
        <nav
          aria-label={t("nav.mobileMenu")}
          className="overflow-y-auto px-4 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onClose}
                data-control-id={`${item.controlId}.mobile-menu`}
                className={({ isActive }) =>
                  `my-1 flex min-h-16 items-center gap-4 rounded-2xl px-4 py-3 text-base font-medium focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`
                }
              >
                <Icon aria-hidden="true" className="size-6 shrink-0" />
                <span className="flex-1">{t(item.labelKey)}</span>
                <ChevronRight aria-hidden="true" className="size-5 opacity-60" />
              </NavLink>
            );
          })}
        </nav>
      </section>
    </div>,
    document.body,
  );
}
